import { connectSocket } from '@/plugins/socket';
import { useAuthStore } from '@/stores/agency/auth';
import { useUserManagementStore } from '@/stores/agency/settings/user-management/user-management';
import { clearForeignSessionLogoutFlag } from '@/utils/cross-tab-session';

/**
 * Hydrates auth and permission state from localStorage
 * Called once during app initialization to restore user session
 *
 * This approach:
 * - Only initializes stores if user data exists (conditional loading)
 * - Separates concerns (auth store handles profile, permission store handles permissions)
 * - Prevents flash of unauthorized content on page reload
 */
export const restoreAuthState = (): void => {
  // A full page load re-syncs this tab to whatever session is currently shared via
  // localStorage, so any earlier "logged out by another tab" flag no longer applies.
  clearForeignSessionLogoutFlag();

  // Only restore if user data exists (user was logged in)
  const savedProfile = localStorage.getItem('userData');
  if (!savedProfile) {
    return;
  }

  // Restore user profile to auth store
  const authStore = useAuthStore();

  authStore.loggedInUserProfile = JSON.parse(savedProfile);

  // Restore permissions to user management store
  const savedPermissions = localStorage.getItem('userPermissions');
  if (savedPermissions) {
    const permissionStore = useUserManagementStore();

    permissionStore.usersPermissionById = JSON.parse(savedPermissions);
  }

  // Reconnect socket for the restored session (autoConnect is disabled on the socket instance)
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    connectSocket(accessToken);
  }
};
